// @flow
import * as React from 'react';
import {defineMessage} from 'react-intl';
import samplePaymentIntent from '../../responses/payment_intent';
import {
  DefaultCards,
  CreateShellCommand,
} from '../../../../components/left-col';

import type {StepsConfig} from '../../../../data/GuideTypes';

export default ([
  {
    file: 'server',
    id: 'setup',
    section: 'setup-stripe',
  },
  {
    file: 'server',
    id: 'create',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.create.heading',
        defaultMessage: 'Create a PaymentIntent',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.create.body',
        defaultMessage: `Add an endpoint on your server that creates a <a>PaymentIntent</a>. A PaymentIntent tracks the customer's payment lifecycle, keeping track of any failed payment attempts and ensuring the customer is only charged once. Return the PaymentIntent's client secret in the response to finish the payment on the client.`,
      }),
      values: {
        a: (str) => (
          <a href="/docs/api/payment_intents" target="_blank">
            {str}
          </a>
        ),
      },
    },
    response: samplePaymentIntent,
  },
  {
    id: 'add-react-modules',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.web.react.add-react-modules.heading',
        defaultMessage: 'Add Stripe to your React app',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.add-react-modules.body',
        defaultMessage:
          'Use the Stripe.js and the Stripe Elements UI library to stay PCI compliant by ensuring that card details go directly to Stripe and never reach your server.',
      }),
    },
    Component: CreateShellCommand(
      'npm install --save @stripe/react-stripe-js @stripe/stripe-js',
    ),
  },
  {
    id: 'load-stripe',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.load-stripe.heading',
        defaultMessage: 'Load Stripe.js',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.load-stripe.body',
        defaultMessage:
          'Call <code>loadStripe()</code> with your Stripe publishable API key to configure the Stripe library.',
      }),
      values: {
        code: (str) => <code>{str}</code>,
      },
    },
  },
  {
    id: 'init-elements',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.init-elements.heading',
        defaultMessage: 'Initialize Stripe Elements',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.init-elements.body',
        defaultMessage:
          'Pass the resulting promise from <code>loadStripe</code> to the Elements provider. This allows the child components to access the Stripe service via the Elements consumer.',
      }),
      values: {
        code: (str) => <code>{str}</code>,
      },
    },
  },
  {
    id: 'init-state',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.init-state.heading',
        defaultMessage: 'Set up the state',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.init-state.body',
        defaultMessage:
          'Initialize some state to keep track of the payment, show errors, and manage the user interface.',
      }),
    },
  },
  {
    id: 'use-stripe',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.use-stripe.heading',
        defaultMessage: 'Store a reference to Stripe',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.use-stripe.body',
        defaultMessage:
          'Access the Stripe library in your CheckoutForm component by using the <code>useStripe()</code> and <code>useElements()</code> hooks. If you need to access Elements via a class component, use the <a>ElementsConsumer</a> instead.',
      }),
      values: {
        code: (str) => <code>{str}</code>,
        a: (str) => (
          <a href="/docs/stripe-js/react#elements-consumer" target="_blank">
            {str}
          </a>
        ),
      },
    },
  },
  {
    id: 'fetch-secret',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.fetch-secret.heading',
        defaultMessage: 'Fetch a PaymentIntent',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.fetch-secret.body',
        defaultMessage:
          'Immediately make a request to the endpoint on your server to create a new PaymentIntent as soon as your checkout page loads. The <code>clientSecret</code> returned by your endpoint is used to complete the payment.',
      }),
      values: {
        code: (str) => <code>{str}</code>,
      },
    },
  },
  {
    id: 'style-element',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.style-element.heading',
        defaultMessage: 'Optional: Style the card input',
      }),
    },
    tabs: {
      type: 'editor',
      active: 'input',
      options: {
        input: {
          type: 'input',
          label: {
            message: defineMessage({
              id:
                'docsShell.integrationBuilder.aap.web.react.style-element.option.input',
              defaultMessage: 'Input fields',
            }),
          },
        },
        button: {
          type: 'button',
          label: {
            message: defineMessage({
              id:
                'docsShell.integrationBuilder.aap.web.react.style-element.option.button',
              defaultMessage: 'Button',
            }),
          },
        },
      },
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.style-element.body',
        defaultMessage:
          "Stripe embeds an iframe to securely collect card details. Customize the iframe by passing a <styleA>style</styleA> object. Use your company's color scheme and font to make it match with the rest of your checkout page. Use custom fonts (e.g. from Google Fonts) by initializing Elements with a <fontSetA>font set</fontSetA>.",
      }),
      values: {
        styleA: (str) => (
          <a target="_blank" href="https://stripe.com/docs/js/appendix/style">
            {str}
          </a>
        ),
        fontSetA: (str) => (
          <a href="/docs/js/elements_object/create#stripe_elements-options-fonts">
            {str}
          </a>
        ),
      },
    },
  },
  {
    id: 'listen-for-change',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.web.react.listen-for-change.heading',
        defaultMessage: 'Optional: Surface card errors',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.listen-for-change.body',
        defaultMessage:
          'Define a function to listen for changes to the card input. Immediately surface card errors (e.g. expiry date in the past) and disable the button if the input is empty.',
      }),
    },
  },
  {
    id: 'render-input',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.render-input.heading',
        defaultMessage: 'Add a CardElement',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.render-input.body',
        defaultMessage:
          'Add a CardElement to your payment form, which embeds an iframe with the necessary input fields to collect the card data. This creates a single input that collects the card number, expiry date, CVC, and postal code. Elements displays localized placeholder text of the postal code field based on your customer\'s <a>browser locale</a> (e.g. showing "ZIP" for U.S. cardholders, "Postcode" for U.K. cardholders).',
      }),
      values: {
        a: (str) => (
          <a
            href="/docs/js/elements_object/create#stripe_elements-options-locale"
            target="_blank"
          >
            {str}
          </a>
        ),
      },
    },
  },
  {
    id: 'complete-payment',
    section: 'complete-the-payment',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.web.react.complete-payment.heading',
        defaultMessage: 'Complete the payment',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.complete-payment.body',
        defaultMessage:
          'When your customer clicks the pay button, call <code>confirmCardPayment()</code> with the PaymentIntent client secret and CardElement. Stripe automatically displays a modal if the card <a>requires authentication</a> like 3D Secure, where the customer must enter a passcode or other piece of identifying information to finalize the purchase.',
      }),
      values: {
        code: (str) => <code>{str}</code>,
        a: (str) => (
          <a href="https://www.youtube.com/watch?v=2kc-FjU2-mY" target="_blank">
            {str}
          </a>
        ),
      },
    },
  },
  {
    id: 'handle-error',
    section: 'complete-the-payment',
    heading: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.handle-error.heading',
        defaultMessage: 'Handle any card errors',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.handle-error.body',
        defaultMessage:
          'If your customer’s card is declined, Stripe.js returns an error. Show that error message to your customer so they can try again with a different card. ',
      }),
    },
  },
  {
    id: 'success',
    section: 'complete-the-payment',
    heading: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.success.heading',
        defaultMessage: 'Show a success message',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.success.body',
        defaultMessage:
          'If no error occurred, tell your customer the payment was successful! For any important post-payment actions (like shipping packages, sending email receipts) we recommend <a>setting up a webhook</a>.',
      }),
      values: {
        a: (str) => (
          <a href="/docs/payments/handling-payment-events" target="_blank">
            {str}
          </a>
        ),
      },
    },
  },
  {
    id: 'run-server',
    file: 'server',
    section: 'test-the-integration',
    heading: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.run-server.heading',
        defaultMessage: 'Run the application',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.run-server.body',
        defaultMessage:
          'Run the React app and the server. Go to <a>localhost:3000/checkout</a> to see your checkout page.',
      }),
      values: {
        a: (str) => (
          <a href="http://localhost:3000/checkout" target="_blank">
            {str}
          </a>
        ),
      },
    },
  },
  {
    id: 'test-cards',
    section: 'test-the-integration',
    heading: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.test-cards.heading',
        defaultMessage: 'Make a test payment',
      }),
    },
    testing: true,
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.test-cards.body',
        defaultMessage:
          'Use a test card number to try your integration. These card numbers work in test mode with any CVC, postal code, and future expiry date. Stripe also has a set of <a>international test cards</a> to test specific postal code formats (e.g. only allow numerical values for U.S. zip codes).',
      }),
      values: {
        a: (str) => (
          <a href="/docs/testing#international-cards" target="_blank">
            {str}
          </a>
        ),
      },
    },
    Component: DefaultCards,
  },
  {
    extension: 'extra',
    id: 'email',
    heading: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.email.heading',
        defaultMessage: 'Send an email receipt',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.email.body',
        defaultMessage:
          'Stripe can send an email receipt to your customer using your brand logo and color theme, which are configurable in <a>the Dashboard</a>.',
      }),
      values: {
        a: (str) => (
          <a
            href="https://dashboard.stripe.com/settings/branding"
            target="_blank"
          >
            {str}
          </a>
        ),
      },
    },
    steps: [
      {
        id: 'email-0',
        heading: {
          message: defineMessage({
            id: 'docsShell.integrationBuilder.aap.web.react.email-0.heading',
            defaultMessage: "Collect the customer's email address",
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsShell.integrationBuilder.aap.web.react.email-0.body',
            defaultMessage:
              'Add an input field to your payment form to collect the email address.',
          }),
        },
      },
      {
        id: 'email-state',
        heading: {
          message: defineMessage({
            id:
              'docsShell.integrationBuilder.aap.web.react.email-state.heading',
            defaultMessage: 'Add email to the state',
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsShell.integrationBuilder.aap.web.react.email-state.body',
            defaultMessage:
              'Add a variable to keep track of the email the customer enters.',
          }),
        },
      },
      {
        id: 'email-1',
        heading: {
          message: defineMessage({
            id: 'docsShell.integrationBuilder.aap.web.react.email-1.heading',
            defaultMessage: 'Provide the email address to Stripe',
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsShell.integrationBuilder.aap.web.react.email-1.body',
            defaultMessage:
              'Pass the provided email address as the receipt_email value. Stripe will send an email receipt when the payment succeeds in live mode (but will not send one in test mode).',
          }),
        },
      },
    ],
  },
  {
    extension: 'extra',
    id: 'save-card',
    heading: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.save-card.heading',
        defaultMessage: 'Save card after payment',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.react.save-card.body',
        defaultMessage:
          'Often used by SaaS or e-commerce businesses with recurring customers.',
      }),
    },
    steps: [
      {
        file: 'server',
        id: 'imports',
        section: 'extend-the-integration',
        heading: {
          message: defineMessage({
            id: 'docsShell.integrationBuilder.aap.web.react.imports.heading',
            defaultMessage: 'Import additional Stripe resources',
          }),
        },
      },
      {
        file: 'server',
        id: 'customer',
        section: 'extend-the-integration',
        heading: {
          message: defineMessage({
            id: 'docsShell.integrationBuilder.aap.web.react.customer.heading',
            defaultMessage: 'Create a customer',
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsShell.integrationBuilder.aap.web.react.customer.body',
            defaultMessage:
              'Stripe stores the card on a <a>Customer</a> object. Create a new Customer before creating a PaymentIntent. You can also store name, email, shipping address, and other details on the Customer.',
          }),
          values: {
            a: (str) => (
              <a href="/docs/api/customers" target="_blank">
                {str}
              </a>
            ),
          },
        },
      },
      {
        file: 'server',
        id: 'pass-customer',
        section: 'extend-the-integration',
        heading: {
          message: defineMessage({
            id:
              'docsShell.integrationBuilder.aap.web.react.pass-customer.heading',
            defaultMessage: 'Add the customer to the PaymentIntent',
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsShell.integrationBuilder.aap.web.react.pass-customer.body',
            defaultMessage:
              'Pass the Customer ID to the PaymentIntent and set <code>setup_future_usage</code> to <code>off_session</code>. <code>setup_future_usage</code> tells Stripe how you plan to use the card — certain regions, such as Europe and India, have requirements around reusing card details. <readMoreA>Read more</readMoreA> about optimizing <code>setup_future_usage</code>, otherwise simply set it to <code>off_session</code>. After the PaymentIntent succeeds, Stripe automatically <attachA>attaches</attachA> the card details (in a <paymentMethodA>PaymentMethod</paymentMethodA> object) to your Customer.',
          }),
          values: {
            code: (str) => <code>{str}</code>,
            readMoreA: (str) => (
              <a
                href="/docs/payments/save-and-reuse-cards-only#web-create-payment-intent-off-session"
                target="_blank"
              >
                {str}
              </a>
            ),
            attachA: (str) => (
              <a href="/docs/api/payment_methods/attach" target="_blank">
                {str}
              </a>
            ),
            paymentMethodA: (str) => (
              <a href="/docs/api/payment_methods" target="_blank">
                {str}
              </a>
            ),
          },
        },
      },
      {
        file: 'server',
        id: 'charge',
        section: 'extend-the-integration',
        heading: {
          message: defineMessage({
            id: 'docsShell.integrationBuilder.aap.web.react.charge.heading',
            defaultMessage: 'Charge the card',
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsShell.integrationBuilder.aap.web.react.charge.body',
            defaultMessage: `When you're ready to charge the card again, create a new PaymentIntent with the Customer ID, the PaymentMethod ID of the card you want to charge, and set the <code>off_session</code> and <code>confirm</code> flags to true.`,
          }),
          values: {
            code: (str) => <code>{str}</code>,
          },
        },
      },
    ],
  },
]: StepsConfig);
